import { Badge } from '@codezo/quote-detail';
import type { Owner, OrderRole } from '../types';

/**
 * Who owes the next move, said from the viewer's side of the table.
 *
 * The same order state produces a different chip in each app — "Your action" to
 * the party that owes it, "Waiting on …" to the other — which is the one thing
 * that lets both apps render this package. So the chip takes BOTH the owner and
 * who is looking, and neither app ever writes the label itself.
 *
 * Nothing renders when `owner` is null. A row with no owner is either finished
 * or waiting on the calendar, and a chip on it would be noise the eye learns to
 * skip — which costs the chips that do matter.
 */
export function OwnerChip({
	owner,
	role,
	/** Terminal state wins over ownership: a delivered row owes nobody anything. */
	done,
	doneLabel = 'Delivered',
}: {
	owner: Owner;
	role: OrderRole;
	done?: boolean;
	doneLabel?: string;
}) {
	if (done) {
		return (
			<Badge tone="success" dot>
				{doneLabel}
			</Badge>
		);
	}
	if (!owner) return null;
	if (owner === role) {
		return (
			<Badge tone="warn" dot>
				Your action
			</Badge>
		);
	}
	return (
		<Badge tone="neutral" dot>
			{owner === 'client' ? 'Waiting on client' : 'Waiting on studio'}
		</Badge>
	);
}
